import React, {
    useRef,
    useState,
    useEffect,
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Slider from 'react-slick';
import {
    disableBodyScroll,
    clearAllBodyScrollLocks,
} from 'body-scroll-lock';

import { Mobile, Desktop } from 'app/components/Responsive';

import XBig from 'app/assets/svg/x_big.component.svg';
import Arrow from '../../../assets/images/new-dish-detail-arrow.component.svg';
import Close from './close.component.svg';
import logoSrc from '../../../assets/images/logo-d.svg';

import './modal-slider.scss';

/**
 * @param {?string || null} props.dialogType тип который отображает старую или новую модалку
 * Может быть 'SMALL_MODAL' || 'BIG_MODAL' || null. Передается в order.js
 */

export default function ModalSlider(props) {
    const {
        onClickClose,
        transitionState,
        title = '',
        initialIndex = 0,
        children,
        fixedChildren = null,
        onChangeSlide = () => {},
        dialogType = 'BIG_MODAL',
        items,
    } = props;

    const [currentSlideIndex, setCurrentSlideIndex] = useState(initialIndex);
    const scrollContentRef = useRef(null);
    const slider = useRef({});

    useEffect(() => {
        if (transitionState === 'entered') {
            setTimeout(() => {
                clearAllBodyScrollLocks();
                const scrollTarget = scrollContentRef.current?.querySelector(`[data-list-index="${initialIndex}"]`);

                if (scrollTarget) {
                    disableBodyScroll(scrollTarget);
                }
            }, 500);
        } else if (transitionState === 'exited') {
            clearAllBodyScrollLocks();
        }
    }, [transitionState]);

    useEffect(() => {
        setCurrentSlideIndex(initialIndex);
        if (transitionState === 'entered') {
            onChangeSlide(initialIndex);
        }
    }, [initialIndex, transitionState]);


    const modalClasses = classNames({
        modal: true,
        [transitionState]: transitionState,
    });

    const modalWrapClasses = classNames({
        'modal-wrap': true,
        [transitionState]: transitionState,
        'modal-wrap--small': dialogType === 'SMALL_MODAL',
    });

    const modalWrapInner = classNames({
        'modal-wrap-inner': true,
        'modal-wrap-inner--small': dialogType === 'SMALL_MODAL',
    });

    const modalContentClasses = classNames({
        'modal-content': true,
        [transitionState]: transitionState,
        'modal-content--small': true,
    });


    const modalHeaderCalsses = classNames({
        'modal-logo-header': true,
        'modal-logo-header--small': dialogType === 'SMALL_MODAL',
    });

    const modalHeaderTextClasses = classNames({
        'modal-header__text': true,
        'modal-header__text--small': dialogType === 'SMALL_MODAL',
    });


    const modalSliderArrowNextClasses = classNames({
        'modal-slider-arrow': true,
        'modal-slider-arrow--next': true,
        'modal-slider-arrow--small': dialogType === 'SMALL_MODAL',
        [transitionState]: transitionState,
    });
    const modalSliderArrowPrevClasses = classNames({
        'modal-slider-arrow': true,
        'modal-slider-arrow--prev': true,
        'modal-slider-arrow--small': dialogType === 'SMALL_MODAL',
        [transitionState]: transitionState,
    });

    const closeButtonSpecificClasses = classNames({
        'modal-close--small': dialogType === 'SMALL_MODAL',
        'modal-close--big': dialogType !== 'SMALL_MODAL',
    });

    const isOpen = transitionState !== 'exited';

    const sliderSettings = {
        dots: true,
        infinite: false,
        easing: 'ease-in-out',
        adaptiveHeight: true,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        initialSlide: initialIndex,

        afterChange: (currentIndex) => {
            setCurrentSlideIndex(currentIndex);
            onChangeSlide(currentIndex);

            clearAllBodyScrollLocks();
            const scrollTarget = scrollContentRef.current.querySelector(`[data-list-index="${currentIndex}"]`);

            if (scrollTarget) {
                disableBodyScroll(scrollTarget);
            }
        },
    };

    return (
        <div styleName={modalClasses}>
            <Desktop>
                <div styleName="modal-silder-arrows">
                    <button
                        type="button"
                        onClick={onClickClose}
                        styleName="modal-close-bg"
                    />

                    <div>
                        <button
                            type="button"
                            styleName={modalSliderArrowPrevClasses}
                            disabled={currentSlideIndex === 0}
                            onClick={() => {
                                if (!slider.current) return;
                                slider.current.slickGoTo(currentSlideIndex - 1);
                            }}
                        >
                            <Arrow />
                        </button>
                    </div>
                    <div>
                        <button
                            type="button"
                            styleName={modalSliderArrowNextClasses}
                            disabled={currentSlideIndex === items.length - 1}
                            onClick={() => {
                                if (!slider.current) return;
                                slider.current.slickGoTo(currentSlideIndex + 1);
                            }}
                        >
                            <Arrow />
                        </button>
                    </div>
                </div>

                <div styleName="modal-wrap modal-wrap--transparent">
                    <div styleName={modalHeaderCalsses}>
                        <div className="header-logo__wrap">
                            <picture>
                                <source media="(min-width: 1024px)" srcSet={logoSrc} />
                                <img alt="EE" className="header-logo" />
                            </picture>
                        </div>
                        <button
                            type="button"
                            onClick={onClickClose}
                            styleName="modal-close"
                        >
                            <Close />
                        </button>
                    </div>
                </div>
                {isOpen && fixedChildren && (
                    <div styleName="modal-fixed-children">
                        {fixedChildren}
                    </div>
                )}
            </Desktop>

            <div styleName={modalWrapClasses}>
                <div styleName={modalWrapInner}>
                    {title && (
                        <div styleName="modal-header">
                            <p styleName={modalHeaderTextClasses}>
                                {title}
                            </p>
                            <Mobile>
                                <button
                                    type="button"
                                    onClick={onClickClose}
                                    styleName="modal-close"
                                >
                                    <XBig />
                                </button>
                            </Mobile>
                        </div>
                    )}
                    <div styleName={modalContentClasses} ref={scrollContentRef}>
                        <button
                            type="button"
                            onClick={onClickClose}
                            styleName={closeButtonSpecificClasses}
                        >
                            <Close />
                        </button>

                        <div styleName="modal-content-content" className="modal-content__scoped">
                            {isOpen && (
                                <Slider
                                    ref={slider}
                                    {...sliderSettings}
                                    key={initialIndex}
                                >
                                    {children}
                                </Slider>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

ModalSlider.propTypes = {
    dialogType: PropTypes.string,
    transitionState: PropTypes.string.isRequired,
    onClickClose: PropTypes.func.isRequired,
    title: PropTypes.string,
    initialIndex: PropTypes.number,
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node,
    ]).isRequired,
    fixedChildren: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node,
    ]),
    onChangeSlide: PropTypes.func,
};
